import { type ReactNode, useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { system404, lorebookBodies } from '../lore'
import { BackdropOrbs } from './BackdropOrbs'
import { DownloadButton } from './DownloadButton'
import { SiteFooter } from './SiteFooter'

export interface Chapter {
  id: string
  label: string
}

/**
 * A nav entry in the left sidebar. Either a leaf (href) or a group with children.
 */
interface NavLeaf {
  label: string
  href: string
  dot?: string
}
interface NavGroup {
  label: string
  leaves: NavLeaf[]
}
type NavItem = NavLeaf | NavGroup

const isGroup = (i: NavItem): i is NavGroup => 'leaves' in i

/** Build the sidebar tree from the lore data: System, Bodies (+moons), Factions. */
function buildNav(activeId: string | null): NavItem[] {
  const items: NavItem[] = []

  items.push({
    label: 'System 404',
    href: '/lorebook',
    dot: '#7cf6ff',
  })

  const bodyLeaves: NavLeaf[] = []
  for (const b of lorebookBodies) {
    bodyLeaves.push({ label: b.name, href: `/lorebook/${b.id}`, dot: b.render.color })
  }
  items.push({ label: 'Bodies', leaves: bodyLeaves })

  // top-level factions only; subfactions live inside their parent faction's page
  const facLeaves: NavLeaf[] = []
  for (const f of system404.factions) {
    facLeaves.push({ label: f.name, href: `/lorebook/${f.id}` })
  }
  items.push({ label: 'Factions', leaves: facLeaves })

  // mark active
  void activeId
  return items
}

function isActive(href: string, activeId: string | null): boolean {
  if (href === '/lorebook') return activeId === null
  return activeId === href.split('/').pop()
}

function NavRow({ item, activeId }: { item: NavLeaf; activeId: string | null }) {
  const active = isActive(item.href, activeId)
  return (
    <Link
      href={item.href}
      className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition ${
        active
          ? 'bg-signal/10 text-white'
          : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
      }`}
    >
      {item.dot && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: item.dot, boxShadow: active ? `0 0 8px ${item.dot}` : 'none' }}
        />
      )}
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

function Sidebar({ activeId }: { activeId: string | null }) {
  const items = buildNav(activeId)
  return (
    <nav className="flex flex-col gap-5">
      {items.map((item, idx) =>
        isGroup(item) ? (
          <div key={idx}>
            <div className="px-2.5 pb-1.5 text-[10px] font-medium uppercase tracking-[0.28em] text-slate-600">
              {item.label}
            </div>
            <div className="flex flex-col gap-0.5">
              {item.leaves.map((leaf, j) => (
                <NavRow key={j} item={leaf} activeId={activeId} />
              ))}
            </div>
          </div>
        ) : (
          <NavRow key={idx} item={item} activeId={activeId} />
        ),
      )}
    </nav>
  )
}

/** A compact <select> nav for small screens where the sidebar is hidden. */
function MobileNav({ activeId }: { activeId: string | null }) {
  const items = buildNav(activeId)
  const [, navigate] = useLocation()
  const flat: NavLeaf[] = []
  for (const i of items) {
    if (isGroup(i)) {
      flat.push({ label: i.label, href: '' })
      flat.push(...i.leaves)
    } else {
      flat.push(i)
    }
  }
  return (
    <label className="flex items-center gap-2 text-xs text-slate-400">
      <span className="uppercase tracking-wider text-slate-600">Go</span>
      <select
        className="flex-1 rounded-md border border-edge bg-void-2 px-2 py-1.5 text-sm text-slate-200"
        value={activeId ? `/lorebook/${activeId}` : '/lorebook'}
        onChange={(e) => {
          const v = e.target.value
          // client-side nav (same path the desktop sidebar Links take),
          // so changing the page doesn't do a full reload.
          if (v) navigate(v)
        }}
      >
        {flat.map((l, i) =>
          l.href === '' ? (
            <option key={i} disabled>
              {l.label}
            </option>
          ) : (
            <option key={i} value={l.href}>
              {l.label}
            </option>
          ),
        )}
      </select>
    </label>
  )
}

/**
 * Highlights the TOC entry for the section currently under the sticky header.
 * The active id is the last (by document order) section whose top has scrolled
 * past the threshold, so it tracks reading position as you scroll. `ids` must be
 * listed in the same order they appear in the document.
 */
function useScrollSpy(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null)
  useEffect(() => {
    const first = ids[0]
    if (!first) return
    const onScroll = () => {
      const threshold = 120 // a touch below the sticky header
      let current = first
      for (const id of ids) {
        const el = document.getElementById(id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= threshold) current = id
      }
      setActive(current)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
    // ids is rebuilt every render; join into a stable key so the effect only
    // re-binds when the actual chapter set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')])
  return active
}

/**
 * The lorebook reader frame: a persistent left sidebar of categories, a main
 * reading column, and (on wide screens) a right "chapters" rail of in-page
 * section anchors. Header carries nav + the disabled download button.
 */
export function LorebookShell({
  activeId,
  title,
  chapters,
  children,
}: {
  activeId: string | null
  title: string
  chapters?: Chapter[]
  children: ReactNode
}) {
  const ids = (chapters ?? []).map((c) => c.id)
  const activeChapter = useScrollSpy(ids)
  return (
    <div className="lorebook-root flex min-h-screen flex-col">
      <BackdropOrbs />
      <header className="sticky top-0 z-30 border-b border-edge bg-void/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <Link href="/" className="text-sm text-slate-300 transition hover:text-signal">
              ← Map
            </Link>
            <span className="text-slate-700">/</span>
            <span className="font-display text-sm text-white">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              href="/characters"
              className="rounded-full border border-signal/40 px-4 py-1.5 text-sm text-signal transition hover:border-signal/60 hover:bg-signal/10 hover:text-white"
            >
              Characters
            </Link>
            <Link
              href="/export"
              className="rounded-full border border-signal/40 px-4 py-1.5 text-sm text-signal transition hover:border-signal/60 hover:bg-signal/10 hover:text-white"
            >
              Export
            </Link>
            <DownloadButton />
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1400px] flex-1 gap-8 px-4 py-8 lg:px-6">
        {/* left sidebar: categories */}
        <aside className="hidden w-56 shrink-0 lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
            <Sidebar activeId={activeId} />
          </div>
        </aside>

        {/* main reading column: capped width so the right rail appearing/vanishing
            doesn't reflow the prose (which reads as 'spacing changing') */}
        <main className="min-w-0 max-w-3xl flex-1">
          <div className="mb-5 lg:hidden">
            <MobileNav activeId={activeId} />
          </div>
          <div className="lore-prose">{children}</div>
        </main>

        {/* right rail: chapters (in-page sections) */}
        {chapters && chapters.length > 1 && (
          <aside className="hidden w-48 shrink-0 xl:block">
            <div className="sticky top-24">
              <div className="mb-2 px-2 text-[10px] font-medium uppercase tracking-[0.28em] text-slate-600">
                On this page
              </div>
              <nav className="flex flex-col gap-0.5 border-l border-edge">
                {chapters.map((c) => (
                  <a
                    key={c.id}
                    href={`#${c.id}`}
                    className={`-ml-px border-l px-3 py-1 text-xs transition ${
                      c.id === activeChapter
                        ? 'border-signal text-slate-100'
                        : 'border-transparent text-slate-500 hover:border-signal/40 hover:text-slate-200'
                    }`}
                  >
                    {c.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}
      </div>
      <SiteFooter />
    </div>
  )
}

/** A titled section block within the reader, rendered with an anchor for the TOC. */
export function LoreSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="lore-section scroll-mt-24">
      <h2>{title}</h2>
      {children}
    </section>
  )
}
